import InputTypesEnum from '../../../Enums/InputTypesEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import stringHelper from '../../../Helpers/stringHelper';
import FreelancerLineItemService from '../../../Services/FreelancerLineItemService';

// Trivial placeholder: the wizard only instantiates windowDescription.itemType when no
// editedItem prop is passed. FreelancerLineItemAdmin always passes a RepeatLineItemData built
// from the selected line item (crudType=Update), so this is never invoked, but it keeps the
// windowDescription contract satisfied.
class RepeatLineItemEditItem {}

// Mirrors the old RepeatLineItemPanel's validityCheckerForEndDate: the End Date must not be
// before the Start Date. GeneralEditWizzardPage's per-field InputWithClear calls this as
// (value, editedItem), so the full item is available; the wizard's "entire form" enablement
// check only passes the single field value (editedItem undefined there), matching the existing
// pattern used in CloseFeeWindowDescription.
const validityCheckerForEndDate = (value, editedItem) => {
  if (!editedItem) return '';
  return new Date(editedItem.StartDate) > new Date(value) ? 'The End Date has to be after the Start Date.' : '';
};

export default class RepeatLineItemWindowDescription {
  static titleForEdit = (item) => `Repeat Line Item (${item.FreelancerLineItemId})`;
  static itemType = RepeatLineItemEditItem;
  static finishButtonLabel = 'Repeat Line Item';

  // Client-side count (not the service response), mirroring the same preview loop shown to the
  // user before they submit.
  static messageOnSuccess = (edited) =>
    `${stringHelper.plural(
      RepeatLineItemWindowDescription.countDates(edited),
      'line item',
      'line items',
      true,
      true
    )} been created by repeating.`;

  // Mirrors the old panel's clickOnSubmit: the whole edited item is posted back. MUST return the
  // promise (wizard contract). The service already parseInts PeriodMonth before posting.
  static serviceFunction = (edited) => FreelancerLineItemService.RepeatLineItem({ ...edited });

  // Mirrors the old panel's changeOfRepeatLineItemData: when StartDate is pushed past EndDate,
  // drag EndDate along with it. Mutates the item in place and returns nothing - the wizard commits
  // the mutated object directly (same pattern as FreelancerLineItemWindowDescription.onClientChange).
  static onStartDateChange(item) {
    if (new Date(item.StartDate) > new Date(item.EndDate)) {
      item.EndDate = item.StartDate;
    }
  }

  // Replicates the old renderDateInfo loop verbatim: walk from StartDate to EndDate (inclusive) in
  // PeriodMonth-month steps. PeriodMonth can be a string after a Select change (same as the old
  // panel); dateTimeHelper.addMonthsToDate already parseInts its argument, and parsing it the same
  // way here keeps the guard consistent with the step. Returns [] for invalid dates or a
  // non-positive period (which would otherwise loop forever).
  static collectDates(item) {
    const start = new Date(item.StartDate);
    const end = new Date(item.EndDate);
    const period = parseInt(item.PeriodMonth);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || !(period > 0)) return [];

    const dates = [];
    let actual = start;
    while (actual < dateTimeHelper.addDaysToDate(end, 1)) {
      dates.push(dateTimeHelper.toYYYYsMMsDD(actual, '-'));
      actual = dateTimeHelper.addMonthsToDate(actual, period);
    }
    return dates;
  }

  static countDates(item) {
    return RepeatLineItemWindowDescription.collectDates(item).length;
  }

  // Live preview text rendered by the Notes field below (recomputed on every item change since
  // fieldName is a function). Wording matches the old renderDateInfo card verbatim.
  static buildDatePreview(item) {
    const dates = RepeatLineItemWindowDescription.collectDates(item);
    if (dates.length === 0) return '';
    return dates.length > 1
      ? `You will create ${stringHelper.numberWithLetter(
          dates.length
        )} line items placed on the following dates: ${dates.join(', ')}`
      : `You will create a line item placed on the following date: ${dates[0]}`;
  }

  static pagesInGeneralEditWizzard = [
    {
      label: 'Repeat Line Item',
      fields: [
        {
          fieldName: (item) => RepeatLineItemWindowDescription.buildDatePreview(item),
          label: 'Preview',
          className: 'col-12',
          type: InputTypesEnum.Notes,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'StartDate',
          label: 'Start Date',
          className: 'col-6',
          type: InputTypesEnum.Date,
          validityChecker: ValidityHelper.forRequiredDateLatterThanToday,
          onFieldChange: RepeatLineItemWindowDescription.onStartDateChange,
        },
        {
          fieldName: 'EndDate',
          label: 'End Date',
          className: 'col-6',
          type: InputTypesEnum.Date,
          validityChecker: validityCheckerForEndDate,
        },
        {
          fieldName: 'PeriodMonth',
          label: 'Repeat Period',
          className: 'col-12',
          type: InputTypesEnum.Select,
          selectvalues: [
            { label: 'Monthly', value: 1, disabled: false },
            { label: 'Bimonthly', value: 2, disabled: false },
            { label: 'Quarterly', value: 3, disabled: false },
          ],
        },
      ],
    },
  ];
}
